import { restClient } from '@/Core'

import { LETTER_COUNTERPARTY_URLS } from '../Contracts/ILetter'
import { recipientOptions } from '../Helpers/letterAddresses'

import type { LetterCollectionResponse, LetterCounterparty } from '../Contracts/ILetter'
import type { LetterRecipientOption } from '../Helpers/letterAddresses'

/** Контактів у одного власника одиниці; з великим запасом — одним запитом. */
const CONTACTS_LIMIT = 100

interface CounterpartyLike {
    title?: string | null
    emails?: unknown
}

interface ContactLike {
    title?: string | null
    email?: string | null
}

/**
 * Підказки адресатів листа (`SPEC-013` §9.4): пошти клієнта чи перевізника й
 * пошти його контактів — двома запитами спільних колекцій. Видалені контакти
 * бек не віддає; чужого власника — теж (правило рядка).
 *
 * Збій — порожні підказки: адресу можна вписати руками, а змінні шаблону
 * (`${emails}`) уже підставили основних адресатів.
 */
export const LoadLetterRecipientsTask = async (
    counterparty: LetterCounterparty,
    ownerUuid: string
): Promise<LetterRecipientOption[]> => {
    const urls = LETTER_COUNTERPARTY_URLS[counterparty]
    const filters = encodeURIComponent(
        JSON.stringify({ and: [{ field: `${counterparty}.uuid`, op: '=', value: ownerUuid }] })
    )

    const loadOwner = async (): Promise<CounterpartyLike | null> => {
        try {
            return (await restClient.get(`${urls.owner}/${encodeURIComponent(ownerUuid)}`)) as unknown as CounterpartyLike | null
        } catch {
            return null
        }
    }

    const loadContacts = async (): Promise<ContactLike[]> => {
        try {
            const raw = await restClient.get(`${urls.contacts}?limit=${CONTACTS_LIMIT}&filters=${filters}`)
            const data = raw as unknown as LetterCollectionResponse<ContactLike> | ContactLike[] | null
            const list = Array.isArray(data) ? data : (data?.items ?? data?.['hydra:member'] ?? [])

            return Array.isArray(list) ? list : []
        } catch {
            return []
        }
    }

    const [owner, contacts] = await Promise.all([loadOwner(), loadContacts()])

    return recipientOptions(typeof owner?.title === 'string' ? owner.title : null, owner?.emails, contacts)
}
